import { useState, useEffect, useCallback } from 'react';
import type { Order, User, Menu, CartItem } from '../types';
import { api } from '../api/client';
import {
  fromISO, formatDateLong, formatDeadline, isOrderable,
  getCalendarWeek, toISO, getMonday,
} from '../utils/dates';
import { formatAllergens } from '../utils/allergens';
import { ChooserModal } from '../components/ChooserModal';
import { useSettings } from '../contexts/SettingsContext';

interface MyOrdersPageProps {
  currentUser: User;
  users: User[];
  addToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

export function MyOrdersPage({ currentUser, users, addToast }: MyOrdersPageProps) {
  const { pickupEnabled } = useSettings();
  const [orders, setOrders] = useState<Order[]>([]);
  const [menus, setMenus] = useState<Menu[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [editingMenu, setEditingMenu] = useState<Menu | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.orders.getAll({ userId: currentUser.id });
      setOrders(data.filter((o) => !o.cancelled || true));

      const futureOrders = data.filter((o) => !o.cancelled);
      if (futureOrders.length > 0) {
        const dates = futureOrders.map((o) => o.dateISO);
        const mondays = [...new Set(dates.map((d) => toISO(getMonday(fromISO(d)))))];
        const allMenus = await Promise.all(
          mondays.map((m) => api.menus.getWeek(m))
        );
        setMenus(allMenus.flat());
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Ladefehler';
      addToast(`Fehler: ${message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [currentUser.id, addToast]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const handleCancel = async (order: Order) => {
    if (!confirm(`Bestellung für ${formatDateLong(fromISO(order.dateISO))} wirklich stornieren?`)) return;
    setCancelling(order.id);
    try {
      await api.orders.cancel(order.id);
      addToast('Bestellung storniert', 'success');
      await loadOrders();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Fehler';
      addToast(`Fehler: ${message}`, 'error');
    } finally {
      setCancelling(null);
    }
  };

  const handleEdit = (order: Order) => {
    const menu = menus.find((m) => m.dateISO === order.dateISO);
    if (!menu) {
      addToast('Speiseplan für diesen Tag nicht gefunden', 'error');
      return;
    }
    setEditingOrder(order);
    setEditingMenu(menu);
  };

  const handleUpdateOrder = async (item: Omit<CartItem, 'tempId'>) => {
    if (!editingOrder) return;
    try {
      await api.orders.update(editingOrder.id, {
        dish: item.dish,
        recipientType: item.recipientType,
        recipientName: item.recipientName || null,
        colleagueId: item.colleagueId || null,
      });
      addToast('Bestellung geändert', 'success');
      setEditingOrder(null);
      setEditingMenu(null);
      await loadOrders();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Fehler';
      addToast(`Fehler: ${message}`, 'error');
    }
  };

  const getUserName = (userId: string): string => {
    const u = users.find((x) => x.id === userId);
    return u ? `${u.firstName} ${u.lastName}` : userId;
  };

  const orderedForMe = (order: Order): boolean =>
    order.recipientType === 'colleague' && order.colleagueId === currentUser.id;

  const getRecipientLabel = (order: Order): string => {
    if (orderedForMe(order)) return `Bestellt von: ${getUserName(order.userId)}`;
    if (order.recipientType === 'self') return '';
    if (order.recipientType === 'colleague') return `Für ${getUserName(order.colleagueId || '')}`;
    return `Gast: ${order.recipientName || ''}`;
  };

  const getPickupCodeLabel = (order: Order): string => {
    if (order.recipientType === 'guest') return order.guestCode || '(kein Code)';
    if (order.recipientType === 'colleague') return ''; // Kollegen-Code bleibt privat
    return currentUser.pickupCode || '(kein Code)';
  };

  // Nutzer sehen ab heute (nur aktuelle + zukünftige Bestellungen),
  // Admins die volle Historie (für Reklamationen)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const visibleOrders = orders.filter((o) => {
    if (currentUser.isAdmin) return true;
    return fromISO(o.dateISO) >= today;
  }).sort((a, b) => b.dateISO.localeCompare(a.dateISO));

  const grouped = new Map<number, Order[]>();
  for (const order of visibleOrders) {
    const cw = getCalendarWeek(fromISO(order.dateISO));
    if (!grouped.has(cw)) grouped.set(cw, []);
    grouped.get(cw)!.push(order);
  }

  if (loading) {
    return (
      <div className="loading-center">
        <div className="spinner" />
        <span>Bestellungen werden geladen…</span>
      </div>
    );
  }

  if (visibleOrders.length === 0) {
    return (
      <div className="loading-center" style={{ minHeight: '12.5rem' }}>
        <div style={{ fontSize: '2.5rem' }}>🛒</div>
        <div style={{ color: 'var(--text-muted)', fontWeight: 600, textAlign: 'center' }}>
          Keine Bestellungen in diesem Monat
          <br />
          <span style={{ fontWeight: 400, fontSize: '0.813rem' }}>
            Wechsle zum Tab "Essen bestellen", um neue Bestellungen aufzugeben.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div>
      {[...grouped.entries()].map(([cw, cwOrders]) => (
        <div key={cw} className="orders-section">
          <div className="orders-section-title">KW {cw}</div>

          {cwOrders.map((order) => {
            const orderable = isOrderable(order.dateISO);
            const recipientLabel = getRecipientLabel(order);
            const pickupCode = getPickupCodeLabel(order);

            return (
              <div key={order.id} className={`order-card ${order.cancelled ? 'cancelled' : ''}`}>
                <div className="order-card-left">
                  <div className="order-card-date">
                    {formatDateLong(fromISO(order.dateISO))}
                  </div>
                  <div className="order-card-dish">{order.dish.name}</div>
                  <div className="order-card-allergens">
                    {recipientLabel ? `${recipientLabel} · ` : 'Für mich bestellt'}
                    {order.dish.allergens.length > 0 && ` · Allergene: ${formatAllergens(order.dish.allergens)}`}
                  </div>
                  {pickupEnabled && !order.cancelled && pickupCode !== '' && (
                    <div className="order-card-code">
                      {pickupCode !== '(kein Code)'
                        ? `Code: ${pickupCode}`
                        : 'Noch kein Abholcode hinterlegt.'}
                    </div>
                  )}
                </div>

                <div className="order-card-right">
                  {order.cancelled ? (
                    <span className="status-pill expired">Storniert</span>
                  ) : orderable ? (
                    <span className="status-pill orderable">
                      Änderbar bis {formatDeadline(order.dateISO).split(' ')[0]}
                    </span>
                  ) : (
                    <span className="status-pill expired">Frist abgelaufen</span>
                  )}

                  {!order.cancelled && (
                    <div className="order-card-actions">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleEdit(order)}
                        disabled={!orderable}
                      >
                        Ändern
                      </button>
                      <button
                        className="btn-cancel-text"
                        onClick={() => handleCancel(order)}
                        disabled={!orderable || cancelling === order.id}
                      >
                        {cancelling === order.id ? '…' : 'Stornieren'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}

      {editingOrder && editingMenu && (
        <ChooserModal
          menu={editingMenu}
          currentUser={currentUser}
          users={users}
          existingOrders={orders}
          cartItems={[]}
          editingOrder={editingOrder}
          onConfirm={handleUpdateOrder}
          onClose={() => { setEditingOrder(null); setEditingMenu(null); }}
        />
      )}
    </div>
  );
}
